<template>
  <div class="item-tile" data-cy="item-tile">
    <header class="item-tile__header">
      <a
        class="item-tile__link"
        :class="{ active: type == SubApplicationType.Add }"
        @click="openApplicationForm"
        data-cy="item-tile-link"
      >
        <font-awesome-icon
          v-if="type == SubApplicationType.Add"
          icon="fa-solid fa-pen-to-square"
          data-cy="item-tile-icon"
        />
        {{ product.BaseName }}
        /
        {{ product.BaseNumber }}
      </a>

      <div class="pull-right">
        <a
          class="item-tile__remove"
          ref="item-tile-remove"
          @click="onRemoveClicked"
          data-cy="item-tile-remove"
        >
          <font-awesome-icon class="remove-icon" icon="fa-solid fa-warning" />
          Delete
        </a>
      </div>
    </header>
    <div class="item-tile__content">
      <a
        class="item-tile__link"
        :class="{ active: type == SubApplicationType.Add }"
        @click="openApplicationForm"
        data-cy="item-tile-content-link"
      >
        {{ product.LabelName }}
        /
        {{ product.LabelNumber }}
      </a>
      <span v-if="product.BrandName" data-cy="item-tile-brand-name"
        >Brand: {{ product.BrandName }}</span
      >
      <span data-cy="item-tile-label-type">{{ product.LabelType }}</span>
      <span
        v-if="product.DistributorCompanyName"
        data-cy="item-tile-distributor-name"
        >Label Company: {{ product.DistributorCompanyName }}</span
      >
      <span data-cy="item-tile-plant"
        >Plant: {{ product.PlantName }} [{{ product.PlantType }}]</span
      >
      <span v-if="product.AgencyId" data-cy="item-tile-agency-id"
        >Ukd Id: {{ product.AgencyId }}</span
      >
      <span
        v-if="product._Consumer || product._Industrial"
        data-cy="item-tile-ci"
        >Consumer/Industrial:
        {{ getConsumerIndustrialString(product, "C", "I") }}</span
      >
      <span v-if="product.ProductShipping" data-cy="item-tile-shipping"
        >Packaging: {{ getPackagingString(product) }}</span
      >

      <div
        v-if="type == SubApplicationType.Terminate"
        class="termination-date-container"
        data-cy="item-tile-termination-date"
      >
        <span
          >Termination Date:
          <DatePicker
            class="termination-date-picker"
            :default-value="defaultTerminationDate"
            :max="new Date()"
            :popup-settings="{ popupClass: 'modal-popup' }"
            @change="terminationDateChanged($event)"
            data-cy="item-tile-termination-date-picker"
          />
        </span>
        <Button
          v-if="terminationChanged && newTerminationDate"
          class="termination-save-button"
          type="button"
          theme-color="primary"
          @click="saveTerminationDate"
          data-cy="item-tile-termination-save-button"
          >Save Changes</Button
        >
      </div>

      <span v-if="product.NumberOfAttachments"
        >Documents: {{ product.NumberOfAttachments }}</span
      >
      <span v-if="product.BarCode">Barcode(s): {{ product.BarCode }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ProductDisplayInCart } from "@/Domain/ProductDisplayInCart";
import { SubApplicationType } from "@/Domain/SubApplicationType";
import {
  getConsumerIndustrialString,
  getPackagingString,
} from "@/shared/helpers/productsHelper";
import {
  DatePicker,
  type DatePickerChangeEvent,
} from "@progress/kendo-vue-dateinputs";
import { computed, ref } from "vue";
import { Button } from "@progress/kendo-vue-buttons";
import { api } from "@/api";
import { useMainStore } from "@/stores/useMainStore";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";

const main = useMainStore();
const notificationStore = useNotificationStore();

const props = defineProps<{
  type: SubApplicationType;
  product: ProductDisplayInCart;
}>();

const emit = defineEmits<{
  (e: "editClicked"): void;
  (e: "removeClicked"): void;
}>();

const terminationChanged = ref(false);
const newTerminationDate = ref<Date | null>(null);

const defaultTerminationDate = computed(() => {
  return props.product.TerminationDate
    ? new Date(props.product.TerminationDate)
    : undefined;
});

const openApplicationForm = () => {
  if (props.type != SubApplicationType.Add) return;
  emit("editClicked");
};

const terminationDateChanged = (e: DatePickerChangeEvent) => {
  newTerminationDate.value = e.value;
  terminationChanged.value = true;
};

const onRemoveClicked = () => {
  emit("removeClicked");
};

const saveTerminationDate = () => {
  terminationChanged.value = false;
  handleResponsePromiseAsync(
    api.get("VueUpdateProductTerminationDate", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      subApplication: props.type,
      productInCartId: props.product.Id.toString(),
      producedInOneId: props.product.ProducedInOneId.toString(),
      terminationDate: newTerminationDate.value!.toLocaleString("en-US"),
      ownsId: props.product.OwnsId.toString(),
    }),
    () => {
      notificationStore.notify({
        type: NotificationTypeEnum.success,
        message: `Termination Date updated successfully`,
        duration: 5000,
      });
    },
    "ProductCartItemTile.vue",
    "saveTerminationDate",
    () => {
      notificationStore.notify({
        type: NotificationTypeEnum.error,
        message: `Error updating Termination Date`,
        duration: 5000,
      });
      terminationChanged.value = true;
    }
  );
};
</script>

<style lang="scss">
.item-tile {
  border: 1px solid #ccc;
  background: #f7f7f7;
  border-radius: 5px;
  padding: 10px;
  margin: 10px;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
  }

  &__link {
    color: #000;
    text-decoration: none;
    font-weight: bold;
    font-size: 1.1rem;
    line-height: 1.2rem;

    &.active {
      color: #337ab7;
      cursor: pointer;
    }
  }

  &__remove {
    color: #dc3545;
    cursor: pointer;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    justify-content: center;
    align-items: center;
  }

  .remove-icon {
    margin-top: -2px;
  }

  &__content {
    display: flex;
    flex-direction: column;
    font-size: 0.9rem;
    line-height: 1.2rem;

    .termination-date-container {
      display: flex;
      align-items: flex-start;
      margin-top: 10px;
      font-size: 0.8rem;
      flex-direction: column;
      gap: 12px;

      .termination-date-picker {
        width: auto;

        .k-input-inner {
          border: 0;
        }
      }

      .termination-save-button {
        width: fit-content;
      }
    }
  }
}
</style>
